import React, { useCallback } from 'react'
import { ViewStyle, View, Text as RNText, TextStyle, Pressable } from 'react-native'
import { useHistory } from 'react-router-dom'
import { Instance } from 'mobx-state-tree'
import { observer } from 'mobx-react-lite'
import { useMediaQuery } from '@mui/material'

import { useStores, FilterTermsProps } from '@trivie/core'
import { QuizMedalRow, color } from '@trivie/ui-mobile'
import { KnowledgeBooster } from '@trivie/ui-web/components/knowledge-booster'
import { AssignedQuiz } from '@trivie/core/src/models/UserQuiz'
import { QuizCardLite } from '@trivie/ui-mobile/components/quiz-card'

export interface QuizzesTabProps {
  searchTerm?: string
  filterTerms?: FilterTermsProps
  clearFilters: () => void
}

export const QuizzesTab = observer((props: QuizzesTabProps) => {
  const { libraryStore, userStore } = useStores()
  const { quizzes } = libraryStore
  const { enterpriseTier } = userStore
  const { clearFilters, searchTerm, filterTerms } = props

  const isMobile = !useMediaQuery('(min-width:800px)')

  const history = useHistory()

  const goToQuiz = (quiz: Instance<typeof AssignedQuiz>) => {
    if (quiz.subscription_user_id) {
      history.push(
        `/library/quiz/?subscription_user_id=${quiz.metadata.subscription_user_id}&topic_id=${quiz.metadata.topic_id}&quiz_id=${quiz.quiz_id}&path_id=${quiz.metadata.path_id}&content_user_id=${quiz.metadata.content_user_id}`,
      )
    } else {
      history.push(
        `/library/quiz/?assignment_id=${quiz.metadata.assignment_id}&topic_id=${quiz.metadata.topic_id}&quiz_id=${quiz.quiz_id}&path_id=${quiz.metadata.path_id}&content_user_id=${quiz.metadata.content_user_id}`,
      )
    }
  }

  const getData = useCallback(() => {
    const fullData = quizzes
      .slice()
      ?.filter(
        (f) => !searchTerm || f.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()),
      )
    if (!filterTerms?.status || filterTerms.status.length === 0) return fullData

    let result: typeof fullData = []
    //not started
    if (filterTerms?.status.includes('1')) {
      result = fullData.filter((f) => f?.completed_attempts === 0)
    }

    //in progress
    if (filterTerms?.status.includes('2')) {
      result = result.concat(fullData.filter((f) => f?.quiz_status === 'In Progress'))
    }

    //completed
    if (filterTerms?.status.includes('3')) {
      result = result.concat(
        fullData.filter((f) => f.completed_attempts && f.completed_attempts > 0),
      )
    }

    //locked
    if (filterTerms?.status.includes('4')) {
      result = result.concat(fullData.filter((f) => f.unlocks_at != null))
    }

    return result
  }, [quizzes, filterTerms, searchTerm])

  const renderQuizItems = (filteredData: any) => {
    if (enterpriseTier) {
      return filteredData.map((q, idx) =>
        q.adaptive ? (
          <KnowledgeBooster />
        ) : (
          <QuizMedalRow
            key={`${q.title}_${idx}`}
            parentTopicLocked={false}
            backgroundColor={q.pack_color}
            quiz={q}
            due={q.due_date}
            maxAttempts={q.max_attempts}
            imgSource={q.topic_image}
            isReinforcement={false}
            onPress={() => goToQuiz(q)}
            style={{ marginBottom: 12 }}
          />
        ),
      )
    } else {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
          {filteredData.map((item) => (
            <QuizCardLite
              image={item?.photo_url ?? ''}
              isMobile={isMobile}
              onClick={() => goToQuiz(item)}
              quiz={item}
            />
          ))}
        </div>
      )
    }
  }

  const filteredData = getData()

  return (
    <div style={{ marginTop: 4 }}>
      {renderQuizItems(filteredData)}
      {filteredData?.length !== quizzes.length ? (
        <View style={{ paddingHorizontal: 40, paddingTop: filteredData?.length === 0 ? 120 : 12 }}>
          <RNText style={FILTER}>
            {`${
              quizzes.length - filteredData?.length
            } other quizzes are not shown due to your active filters.`}
            <Pressable onPress={() => clearFilters()}>
              <RNText style={{ ...FILTER, ...CLEAR }}>{'Clear Filters?'}</RNText>
            </Pressable>
          </RNText>
        </View>
      ) : null}
    </div>
  )
})

const FILTER: TextStyle = {
  alignSelf: 'center',
  textAlign: 'center',
  fontStyle: 'italic',
  color: color.shade50,
}
const CLEAR: TextStyle = {
  alignSelf: 'center',
  paddingTop: 40,
  paddingLeft: 4,
  textDecorationLine: 'underline',
}
